import algorithms from '@containers/widget/constants/algorithms';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useAppSelector } from '@redux/hooks';
import { getAnonymousId } from '@redux/settings';
import { CtaButton } from '@tkww/orion-web-ds-action';
import { TextBody } from '@tkww/orion-web-ds-text';
import React, { useCallback, useContext } from 'react';
import TrackableContainer from '../../../../../client/components/trackable_container';
import AnalyticsConstants from '../../../../../constants/analytics';
import { getMcvrmMarketCode } from '../../../../../lib/mcvrm/hooks/useCategories';
import { getVendorTerm } from '../../../../../lib/mcvrm/utils';
import { WeddingsContextProvider } from '../../../../contexts/WeddingsContext/WeddingsContext';
import {
	CategoryRecommendationsContext,
	CategoryRecommendationsProvider,
} from './CategoryRecommendationsProvider';
import Styles from './McVrmLiteWidget.scss';
import { CategoryPills } from './components/CategoryPills';
import { VendorList } from './components/VendorList';
import { MCVRM_SOURCE_CONTENT } from './types';
import {
	getSeeAllUrlForSelectedCategory,
	useCommonTrackingProperties,
} from './utils';

const McVrmLiteWidgetContent = () => {
	const {
		selectedCategoryVendors,
		selectedCategoryCode,
		isLoading,
		shouldShowNoResults,
		categories,
	} = useContext(CategoryRecommendationsContext);
	const storefrontVendorRaw = useAppSelector((state) => state.vendor.vendorRaw);
	const searchPageLocation = useAppSelector(
		(state) => state.searchPageLocation,
	);
	const { track } = useAnalyticsContext();
	const commonTrackingProperties = useCommonTrackingProperties();

	const handleSeeAllClick = useCallback(() => {
		track({
			event: AnalyticsConstants.CLICK_THROUGH_TO_VENDOR_CATEGORY,
			properties: {
				vendorCategoryCode: selectedCategoryCode,
				sourcePage: 'unpaid storefront',
				sourceContent: MCVRM_SOURCE_CONTENT,
			},
		});
	}, [track, selectedCategoryCode]);

	if (categories.length === 0 && !isLoading) {
		return null;
	}

	const seeAllUrl = getSeeAllUrlForSelectedCategory(
		storefrontVendorRaw,
		selectedCategoryCode,
		searchPageLocation,
	);

	const widgetViewedTrackingData = () => {
		return {
			properties: commonTrackingProperties,
			event: AnalyticsConstants.WIDGET_VIEWED,
		};
	};

	return (
		<TrackableContainer
			trackingData={widgetViewedTrackingData()}
			shouldTrack={!isLoading && selectedCategoryVendors.length > 0}
		>
			<div
				className={Styles.container}
				data-testid="mcvrm-lite-widget"
				data-algorithm-type={algorithms.PVREC_CROSS_CAT_ROS}
			>
				<div className={Styles.header}>
					<div className={Styles.headerText}>
						<TextBody size="lg" bold>
							Popular with couples like you
						</TextBody>
						<TextBody size="sm">Check out our featured vendors.</TextBody>
					</div>
				</div>
				<CategoryPills />
				<VendorList />
				{!shouldShowNoResults && !isLoading && (
					<CtaButton
						as="a"
						styleType="primary"
						size="md"
						href={seeAllUrl}
						className={Styles.headerAction}
						onClick={handleSeeAllClick}
					>
						Browse all {getVendorTerm(selectedCategoryCode, true, true)}
					</CtaButton>
				)}
			</div>
		</TrackableContainer>
	);
};

export const McVrmLiteWidget = () => {
	const memberId = useAppSelector(
		(state) => state.membership.member?.id || null,
	);
	const anonymousId = useAppSelector(getAnonymousId);
	const vendorRaw = useAppSelector((state) => state.vendor.vendorRaw);
	const marketCode = useAppSelector(getMcvrmMarketCode);

	return (
		<WeddingsContextProvider>
			<CategoryRecommendationsProvider
				vendorId={vendorRaw?.id ?? null}
				memberId={memberId}
				anonymousId={anonymousId}
				excludeCategoryCode={vendorRaw?.categories?.[0]?.code}
				marketCode={marketCode}
			>
				<McVrmLiteWidgetContent />
			</CategoryRecommendationsProvider>
		</WeddingsContextProvider>
	);
};
